/**
 * WEB-693 / DES-69 (O-7) — SSR-readable theme resolution.
 *
 * Source of truth is the first-party `loci-theme` cookie (SameSite=Lax,
 * 1 year, not httpOnly). `localStorage['loci-preview-theme']` is a
 * one-release migration read only and is never written as the SoT.
 *
 * Resolution order (first match wins):
 *   1. Forced surface — `(city)` marketing-city host and `(admin)`
 *   2. `loci-theme` cookie
 *   3. `prefers-color-scheme` (client, anonymous / no cookie)
 *   4. Dark — brand default
 *
 * `?theme=light` is board plumbing for design renderings only and must
 * never be read here.
 */

export type ThemeMode = 'light' | 'dark';

export type SsrThemeSource = 'forced' | 'cookie' | 'default';

export interface SsrHtmlTheme {
  theme: ThemeMode;
  source: SsrThemeSource;
}

export const THEME_COOKIE_NAME = 'loci-theme';

/** Retired SoT — read once to migrate, never write. */
export const THEME_MIGRATION_STORAGE_KEY = 'loci-preview-theme';

export const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export function parseThemeMode(value: string | undefined | null): ThemeMode | null {
  if (value === 'light' || value === 'dark') return value;
  return null;
}

export function isForcedDarkAdminPath(pathname: string): boolean {
  const path = pathname.split('?')[0] ?? '';
  return path === '/admin' || path.startsWith('/admin/');
}

export function resolveSsrHtmlTheme(input: {
  forcedDark: boolean;
  cookieValue: string | undefined | null;
}): SsrHtmlTheme {
  if (input.forcedDark) {
    return { theme: 'dark', source: 'forced' };
  }
  const cookie = parseThemeMode(input.cookieValue);
  if (cookie) {
    return { theme: cookie, source: 'cookie' };
  }
  return { theme: 'dark', source: 'default' };
}

export function serializeThemeCookie(mode: ThemeMode): string {
  const attributes = [
    `${THEME_COOKIE_NAME}=${mode}`,
    'Path=/',
    `Max-Age=${THEME_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    attributes.push('Secure');
  }
  return attributes.join('; ');
}

export function readThemeMigrationStorage(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  try {
    return parseThemeMode(window.localStorage.getItem(THEME_MIGRATION_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Client-only default-source resolution (step 3 of the O-7 order).
 * Returns `null` on the server and when neither migration storage nor
 * `prefers-color-scheme: light` applies — callers keep the SSR dark default.
 */
export function resolveClientDefaultTheme(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  const migrated = readThemeMigrationStorage();
  if (migrated) return migrated;
  try {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  } catch {
    return null;
  }
  return null;
}

/** Pure next-mode for a new server payload (`source` + `ssrTheme` change). */
export function resolveModeForSsrSource(source: SsrThemeSource, ssrTheme: ThemeMode): ThemeMode {
  if (source === 'forced') return 'dark';
  if (source === 'cookie') return ssrTheme;
  return resolveClientDefaultTheme() ?? ssrTheme;
}
